import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { JepCurrentAccountService } from '@jepto/shared/services/jep-current-account/jep-current-account.service';
import { JepRequestService } from '@jepto/shared/services/jep-request/jep-request.service';
import { warn } from '@jepto/shared/utils/logger';
import type { TableCardConfig } from '../../../../models/table-config.model';
import type { TableData } from '../../../../models/chart-data.model';
import { fetchGetCardDataItem, iamOnlyGraphqlAccountId } from '../../../../graphql/queries/fetch-get-card-data.util';
import {
	AnalyticsDataLatencyService,
	dataSourceServicesFromCardConfig,
} from '../../../../services/analytics-data-latency.service';
import { AnalyticsDataRequesterService } from '../../../../services/analytics-data-requester.service';
import { DataService } from '../../../../services/data.service';
import {
	extractRawCardDataFromGql,
	hasRenderableTablePayload,
	normalizeTableCardDataResponse,
	publishTablePreviewDataToDataService,
	getTablePayloadExecutionId,
	resolveRenderableTablePreviewPayload,
	stampTableDataRequestedPagination,
	tableDataFirstRowFingerprint,
	tableDataPayloadFingerprint,
} from './table-preview-data.util';
import {
	beginPreviewDataLoaderWsWait,
	clearPreviewWsWaitState,
	createPreviewWsWaitState,
	runPreviewWaitForPersistedDataOrFetch,
	type CancelInFlightOptions,
	type PreviewWsWaitState,
} from '../../shared/utils/card-preview-loader-wait.util';
import type { PreviewDataFetchOptions } from '../../shared/utils/card-editor-preview-data-fetch.util';
import { orderConfigToAwsJsonForGql } from '../../shared/data-tab/chart-data-order.util';

export interface TablePreviewFetchOptions extends PreviewDataFetchOptions {
	pagination?: { offset: number; limit: number };
	/** Runtime Cube order map; serialized to dataConfig.order AWSJSON for getCardData. */
	order?: Record<string, 'asc' | 'desc'>;
}

export interface TablePreviewDataLoaderCallbacks {
	onRefreshError?: (message: string) => void;
	onWsWaitTimeout?: (message: string) => void;
}

/**
 * Shared root loader for table `getCardData`. Request/WS wait state is tracked
 * **per cardId** so report-canvas paging on one table does not cancel sibling cards.
 */
@Injectable({ providedIn: 'root' })
export class TablePreviewDataLoader {
	private readonly requestService = inject(JepRequestService);
	private readonly currentAccount = inject(JepCurrentAccountService, {
		optional: true,
	});
	private readonly dataService = inject(DataService);
	private readonly analyticsDataLatency = inject(AnalyticsDataLatencyService);
	private readonly analyticsDataRequester = inject(
		AnalyticsDataRequesterService
	);

	readonly isRefreshing = signal(false);
	readonly wsWaitRevision = signal(0);

	private requestTokenCounter = 0;
	private destroyed = false;
	private deferredRefresh = false;

	/** Active GraphQL fetch token per card. */
	private readonly activeRequestByCard = new Map<string, number>();
	/** WS wait state per card (report canvas can page multiple tables). */
	private readonly wsWaitByCard = new Map<string, PreviewWsWaitState>();
	/**
	 * Pagination owned by an in-flight `fetchAndWait` per card. A competing
	 * `fetch` with a different offset must not cancel that wait.
	 */
	private readonly fetchAndWaitPaginationByCard = new Map<
		string,
		{ offset: number; limit: number }
	>();
	/** Coalesce concurrent `fetchAndWait` for the same card + pagination slice. */
	private readonly inFlightFetchAndWait = new Map<
		string,
		Promise<TableData>
	>();

	fetch(
		cardId: string,
		timeDimension: Record<string, unknown>,
		cardConfig: TableCardConfig,
		callbacks?: TablePreviewDataLoaderCallbacks,
		options?: TablePreviewFetchOptions
	): void {
		if (this.destroyed) {
			return;
		}
		const waitingPagination = this.fetchAndWaitPaginationByCard.get(cardId);
		const nextPagination = options?.pagination
			? {
					offset: Math.max(0, options.pagination.offset),
					limit: Math.max(1, options.pagination.limit),
				}
			: undefined;
		if (waitingPagination) {
			if (
				!nextPagination ||
				waitingPagination.offset !== nextPagination.offset ||
				waitingPagination.limit !== nextPagination.limit
			) {
				warn(
					'[TablePreviewDataLoader] Refusing fetch that would cancel in-flight page wait',
					{ cardId, waitingPagination, nextPagination }
				);
				return;
			}
		}
		const refreshBaselineExecutionId = options?.refresh
			? this.readTableExecutionId(cardId)
			: undefined;
		this.#cancelCard(cardId, { preserveRefreshing: true });
		const requestToken = ++this.requestTokenCounter;
		this.activeRequestByCard.set(cardId, requestToken);
		this.#syncRefreshing();
		const waitBaselineFingerprint = this.readTableFingerprint(cardId);
		const accountId = this.currentAccount?.getAccountId?.()?.trim();
		void this.#executeFetch(
			cardId,
			timeDimension,
			cardConfig,
			requestToken,
			waitBaselineFingerprint,
			callbacks,
			accountId,
			options,
			refreshBaselineExecutionId
		);
	}

	/**
	 * Like {@link fetch}, but resolves when inline/WS table data is ready.
	 * Drives `tryCompleteWsWait` itself so report-canvas paging works without
	 * the table editor effect.
	 */
	async fetchAndWait(
		cardId: string,
		timeDimension: Record<string, unknown>,
		cardConfig: TableCardConfig,
		options?: TablePreviewFetchOptions
	): Promise<TableData> {
		const requestedPagination = options?.pagination
			? {
					offset: Math.max(0, options.pagination.offset),
					limit: Math.max(1, options.pagination.limit),
				}
			: undefined;
		const waitKey = this.#fetchAndWaitKey(
			cardId,
			requestedPagination,
			options?.order
		);
		const inFlight = this.inFlightFetchAndWait.get(waitKey);
		if (inFlight) {
			return inFlight;
		}
		const run = this.#fetchAndWaitImpl(
			cardId,
			timeDimension,
			cardConfig,
			requestedPagination,
			options
		);
		this.inFlightFetchAndWait.set(waitKey, run);
		try {
			return await run;
		} finally {
			if (this.inFlightFetchAndWait.get(waitKey) === run) {
				this.inFlightFetchAndWait.delete(waitKey);
			}
		}
	}

	#fetchAndWaitKey(
		cardId: string,
		pagination: { offset: number; limit: number } | undefined,
		order?: Record<string, 'asc' | 'desc'>
	): string {
		const orderKey = order
			? Object.keys(order)
					.sort()
					.map(k => `${k}:${order[k]}`)
					.join(',')
			: '';
		if (!pagination) {
			return `${cardId}|${orderKey}`;
		}
		return `${cardId}|${pagination.offset}|${pagination.limit}|${orderKey}`;
	}

	async #fetchAndWaitImpl(
		cardId: string,
		timeDimension: Record<string, unknown>,
		cardConfig: TableCardConfig,
		requestedPagination: { offset: number; limit: number } | undefined,
		options?: TablePreviewFetchOptions
	): Promise<TableData> {
		if (this.destroyed) {
			throw new Error('[TablePreviewDataLoader] destroyed');
		}
		const baselineFingerprint = this.readTableFingerprint(cardId);
		const baselineExecutionId = this.readTableExecutionId(cardId);
		const baselineResolved = resolveRenderableTablePreviewPayload(
			this.dataService,
			cardId
		);
		const baselineFirstRow =
			baselineResolved && hasRenderableTablePayload(baselineResolved)
				? tableDataFirstRowFingerprint(baselineResolved as TableData)
				: '';
		if (requestedPagination) {
			this.fetchAndWaitPaginationByCard.set(cardId, requestedPagination);
		}
		try {
			this.fetch(cardId, timeDimension, cardConfig, undefined, options);
			const requestToken = this.activeRequestByCard.get(cardId);

			const deadline = Date.now() + 30_000;
			while (Date.now() < deadline) {
				if (this.destroyed) {
					throw new Error('[TablePreviewDataLoader] destroyed');
				}
				this.#assertFetchAndWaitStillOwned(
					cardId,
					requestedPagination
				);
				this.tryCompleteWsWait(
					cardId,
					this.getWsWaitRequestTokenForCard(cardId) ?? requestToken
				);

				const resolved = resolveRenderableTablePreviewPayload(
					this.dataService,
					cardId
				);
				const hasRenderable =
					!!resolved && hasRenderableTablePayload(resolved);
				const fingerprint = hasRenderable
					? tableDataPayloadFingerprint(resolved)
					: '';
				const executionId = hasRenderable
					? getTablePayloadExecutionId(resolved)
					: '';
				const isNewer =
					hasRenderable &&
					((executionId !== '' &&
						baselineExecutionId !== '' &&
						executionId !== baselineExecutionId) ||
						(fingerprint !== '' &&
							fingerprint !== baselineFingerprint));

				if (isNewer && resolved) {
					this.#assertFetchAndWaitStillOwned(
						cardId,
						requestedPagination
					);
					const asTable = resolved as TableData;
					if (
						requestedPagination &&
						requestedPagination.offset > 0 &&
						baselineFirstRow !== ''
					) {
						const candidateFirstRow =
							tableDataFirstRowFingerprint(asTable);
						if (candidateFirstRow === baselineFirstRow) {
							// Stale page-1 WS while waiting for a later slice.
							if (!this.#isCardBusy(cardId)) {
								throw new Error(
									`[TablePreviewDataLoader] getCardData finished without new table data (${cardId})`
								);
							}
							await new Promise<void>(resolve =>
								setTimeout(resolve, 50)
							);
							continue;
						}
					}
					const rawOffset = asTable.data?.offset;
					const payloadOffset =
						typeof rawOffset === 'number' && !Number.isNaN(rawOffset)
							? Math.max(0, rawOffset)
							: undefined;
					// Later-page rows must not be stamped as offset 0. Only overwrite
					// when the backend echoes 0 for a non-zero page wait.
					if (
						requestedPagination &&
						payloadOffset !== undefined &&
						payloadOffset !== requestedPagination.offset &&
						!(
							requestedPagination.offset > 0 && payloadOffset === 0
						)
					) {
						await new Promise<void>(resolve =>
							setTimeout(resolve, 50)
						);
						continue;
					}
					let stamped = stampTableDataRequestedPagination(
						asTable,
						requestedPagination
					);
					if (
						requestedPagination &&
						payloadOffset === 0 &&
						requestedPagination.offset > 0
					) {
						stamped = {
							...asTable,
							data: {
								...asTable.data,
								offset: requestedPagination.offset,
								limit: requestedPagination.limit,
							},
						};
					}
					if (stamped !== asTable) {
						publishTablePreviewDataToDataService(
							this.dataService,
							cardId,
							stamped
						);
					}
					this.#cancelCard(cardId);
					return stamped;
				}

				if (!this.#isCardBusy(cardId)) {
					const fallback = resolveRenderableTablePreviewPayload(
						this.dataService,
						cardId
					);
					if (fallback && hasRenderableTablePayload(fallback)) {
						return fallback as TableData;
					}
					throw new Error(
						`[TablePreviewDataLoader] getCardData finished without new table data (${cardId})`
					);
				}

				await new Promise<void>(resolve => setTimeout(resolve, 50));
			}

			this.#cancelCard(cardId);
			throw new Error(
				`[TablePreviewDataLoader] Timed out waiting for table data (${cardId})`
			);
		} finally {
			if (
				requestedPagination &&
				this.fetchAndWaitPaginationByCard.get(cardId) ===
					requestedPagination
			) {
				this.fetchAndWaitPaginationByCard.delete(cardId);
			}
		}
	}

	async #executeFetch(
		cardId: string,
		timeDimension: Record<string, unknown>,
		cardConfig: TableCardConfig,
		requestToken: number,
		waitBaselineFingerprint: string | null,
		callbacks: TablePreviewDataLoaderCallbacks | undefined,
		accountId: string | undefined,
		options?: TablePreviewFetchOptions,
		refreshBaselineExecutionId?: string
	): Promise<void> {
		const { requestId, requester } =
			await this.analyticsDataRequester.buildRequestContext();
		this.analyticsDataLatency.markRequest({
			requestId,
			operation: 'getCardData',
			cardId,
			...(accountId ? { accountId } : {}),
			dataSourceServices: dataSourceServicesFromCardConfig(cardConfig),
		});
		try {
			const orderAwsJson = orderConfigToAwsJsonForGql(options?.order);
			const response = await fetchGetCardDataItem(
				(query, variables) =>
					firstValueFrom(
						this.requestService.query({
							query,
							variables,
							fetchPolicy: 'network-only',
						})
					),
				{
					cardId,
					timeDimension,
					accountId: iamOnlyGraphqlAccountId(this.currentAccount),
					refresh: options?.refresh,
					pagination: options?.pagination,
					...(orderAwsJson
						? { dataConfig: { order: orderAwsJson } }
						: {}),
				},
				{ requestId, requester }
			);
			if (!this.#isActiveRequest(cardId, requestToken)) {
				return;
			}
			const rawCardData = extractRawCardDataFromGql(response);
			if (typeof rawCardData === 'boolean') {
				if (rawCardData) {
					this.beginWsWait(
						cardId,
						requestToken,
						waitBaselineFingerprint ?? '',
						callbacks,
						refreshBaselineExecutionId
					);
				} else {
					warn(
						'[TablePreviewDataLoader] getCardData returned false',
						{ cardId, timeDimension }
					);
					this.finalizeRequest(cardId, requestToken);
				}
				return;
			}
			const normalized = normalizeTableCardDataResponse(
				response,
				cardConfig
			);
			if (!normalized || !hasRenderableTablePayload(normalized)) {
				warn(
					'[TablePreviewDataLoader] getCardData returned no inline table payload',
					{
						cardId,
						timeDimension,
						rawType: typeof rawCardData,
					}
				);
				this.finalizeRequest(cardId, requestToken);
				return;
			}
			publishTablePreviewDataToDataService(
				this.dataService,
				cardId,
				normalized
			);
			const wait = this.wsWaitByCard.get(cardId);
			if (wait?.wsWaitRequestToken !== requestToken) {
				this.finalizeRequest(cardId, requestToken);
			}
		} catch {
			if (!this.#isActiveRequest(cardId, requestToken)) {
				return;
			}
			this.#clearWsWait(cardId);
			this.finalizeRequest(cardId, requestToken);
			callbacks?.onRefreshError?.(
				'Unable to refresh table preview. Please try again.'
			);
		}
	}

	/** After autosave: wait for WS table data before calling `getCardData`. */
	waitForPersistedDataOrFetch(
		cardId: string,
		timeDimension: Record<string, unknown>,
		cardConfig: TableCardConfig,
		callbacks?: TablePreviewDataLoaderCallbacks
	): void {
		runPreviewWaitForPersistedDataOrFetch(
			{
				cancelInFlight: options => {
					this.#cancelCard(cardId, options);
				},
				assignRequestToken: () => {
					const requestToken = ++this.requestTokenCounter;
					this.activeRequestByCard.set(cardId, requestToken);
					return requestToken;
				},
				setRefreshing: refreshing => {
					if (refreshing) {
						this.deferredRefresh = true;
					}
					this.#syncRefreshing();
				},
				readBaselineFingerprint: id => this.readTableFingerprint(id),
				beginWsWait: (id, token, baseline, wsCallbacks) =>
					this.beginWsWait(id, token, baseline, wsCallbacks),
				fetch: (id, td, cfg, fetchCallbacks) =>
					this.fetch(id, td, cfg, fetchCallbacks),
				isActiveRequest: token => this.#isActiveRequest(cardId, token),
			},
			cardId,
			timeDimension,
			cardConfig,
			callbacks
		);
	}

	/** Arm preview progress bar while autosave is still pending (deferred fetch). */
	beginDeferredRefresh(): void {
		if (this.destroyed) return;
		this.deferredRefresh = true;
		this.#syncRefreshing();
	}

	/** Clear deferred progress bar only when no fetch/WS wait owns the cycle. */
	cancelDeferredRefresh(): void {
		if (this.destroyed) return;
		this.deferredRefresh = false;
		this.#syncRefreshing();
	}

	tryCompleteWsWait(
		cardId: string,
		requestToken: number | undefined
	): boolean {
		if (!cardId || requestToken === undefined) {
			return false;
		}
		const wait = this.wsWaitByCard.get(cardId);
		if (!wait || wait.wsWaitRequestToken !== requestToken) {
			return false;
		}
		const resolved = resolveRenderableTablePreviewPayload(
			this.dataService,
			cardId
		);
		if (!resolved || !hasRenderableTablePayload(resolved)) {
			return false;
		}
		if (!this.#payloadMatchesOwnedPagination(cardId, resolved as TableData)) {
			return false;
		}
		const baselineExecutionId = wait.wsWaitBaselineExecutionId;
		if (baselineExecutionId !== undefined) {
			const executionId = getTablePayloadExecutionId(resolved);
			if (!executionId || executionId === baselineExecutionId) {
				return false;
			}
		} else {
			const fingerprint = tableDataPayloadFingerprint(resolved);
			if (fingerprint === wait.wsWaitBaselineFingerprint) {
				return false;
			}
		}
		this.#clearWsWait(cardId);
		this.finalizeRequest(cardId, requestToken);
		return true;
	}

	getWsWaitCardId(): string | undefined {
		for (const [id, wait] of this.wsWaitByCard) {
			if (wait.wsWaitRequestToken !== undefined) {
				return id;
			}
		}
		return undefined;
	}

	getWsWaitRequestToken(): number | undefined {
		const cardId = this.getWsWaitCardId();
		return cardId ? this.getWsWaitRequestTokenForCard(cardId) : undefined;
	}

	getWsWaitRequestTokenForCard(cardId: string): number | undefined {
		return this.wsWaitByCard.get(cardId)?.wsWaitRequestToken;
	}

	cancel(options?: CancelInFlightOptions): void {
		for (const cardId of [...this.activeRequestByCard.keys()]) {
			this.#cancelCard(cardId, { preserveRefreshing: true });
		}
		for (const cardId of [...this.wsWaitByCard.keys()]) {
			this.#clearWsWait(cardId);
		}
		this.fetchAndWaitPaginationByCard.clear();
		this.deferredRefresh = false;
		if (!options?.preserveRefreshing) {
			this.#syncRefreshing();
		}
	}

	/**
	 * Cancels in-flight work for one card without marking the root loader destroyed.
	 * Prefer this from editor teardown — {@link destroy} permanently disables
	 * report-canvas paging that shares this singleton.
	 */
	cancelCard(cardId: string, options?: CancelInFlightOptions): void {
		this.#cancelCard(cardId, options);
		this.fetchAndWaitPaginationByCard.delete(cardId);
	}

	/**
	 * Permanently disables this root singleton. Prefer {@link cancelCard} from
	 * component teardown; reserve this for app/test shutdown only.
	 */
	destroy(): void {
		this.destroyed = true;
		this.cancel();
	}

	private beginWsWait(
		cardId: string,
		requestToken: number,
		waitBaselineFingerprint: string,
		callbacks?: TablePreviewDataLoaderCallbacks,
		waitBaselineExecutionId?: string
	): void {
		let wait = this.wsWaitByCard.get(cardId);
		if (!wait) {
			wait = createPreviewWsWaitState();
			this.wsWaitByCard.set(cardId, wait);
		}
		wait.wsWaitBaselineExecutionId = waitBaselineExecutionId;
		beginPreviewDataLoaderWsWait({
			state: wait,
			cardId,
			requestToken,
			waitBaselineFingerprint,
			hasUpdatedRenderableData: () => {
				const current = resolveRenderableTablePreviewPayload(
					this.dataService,
					cardId
				);
				if (!current || !hasRenderableTablePayload(current)) {
					return false;
				}
				if (waitBaselineExecutionId !== undefined) {
					const executionId = getTablePayloadExecutionId(current);
					return (
						executionId !== '' &&
						executionId !== waitBaselineExecutionId
					);
				}
				return (
					tableDataPayloadFingerprint(current) !==
					waitBaselineFingerprint
				);
			},
			finalizeRequest: token => this.finalizeRequest(cardId, token),
			isActiveRequest: token => this.#isActiveRequest(cardId, token),
			bumpRevision: () => this.wsWaitRevision.update(n => n + 1),
			callbacks,
		});
		this.#syncRefreshing();
	}

	#clearWsWait(cardId: string): void {
		const wait = this.wsWaitByCard.get(cardId);
		if (!wait) {
			return;
		}
		clearPreviewWsWaitState(wait, () =>
			this.wsWaitRevision.update(n => n + 1)
		);
		this.wsWaitByCard.delete(cardId);
	}

	private readTableFingerprint(cardId: string): string {
		const current = this.dataService.tableDataSignal(cardId)();
		return tableDataPayloadFingerprint(current);
	}

	private readTableExecutionId(cardId: string): string {
		return getTablePayloadExecutionId(
			resolveRenderableTablePreviewPayload(this.dataService, cardId)
		);
	}

	private finalizeRequest(cardId: string, requestToken: number): void {
		if (this.activeRequestByCard.get(cardId) !== requestToken) return;
		this.activeRequestByCard.delete(cardId);
		this.deferredRefresh = false;
		this.#syncRefreshing();
	}

	#isActiveRequest(cardId: string, requestToken: number): boolean {
		return (
			!this.destroyed &&
			this.activeRequestByCard.get(cardId) === requestToken
		);
	}

	#payloadMatchesOwnedPagination(
		cardId: string,
		payload: TableData
	): boolean {
		const waiting = this.fetchAndWaitPaginationByCard.get(cardId);
		if (!waiting) {
			return true;
		}
		const rawOffset = payload.data?.offset;
		if (typeof rawOffset !== 'number' || Number.isNaN(rawOffset)) {
			return true;
		}
		const payloadOffset = Math.max(0, rawOffset);
		if (payloadOffset === waiting.offset) {
			return true;
		}
		return waiting.offset > 0 && payloadOffset === 0;
	}

	#assertFetchAndWaitStillOwned(
		cardId: string,
		requestedPagination: { offset: number; limit: number } | undefined
	): void {
		if (!requestedPagination) {
			return;
		}
		if (
			this.fetchAndWaitPaginationByCard.get(cardId) !==
			requestedPagination
		) {
			throw new Error(
				`[TablePreviewDataLoader] fetchAndWait superseded (${cardId})`
			);
		}
	}

	#isCardBusy(cardId: string): boolean {
		if (this.activeRequestByCard.has(cardId)) {
			return true;
		}
		const wait = this.wsWaitByCard.get(cardId);
		return wait?.wsWaitRequestToken !== undefined;
	}

	#cancelCard(cardId: string, options?: CancelInFlightOptions): void {
		this.activeRequestByCard.delete(cardId);
		this.#clearWsWait(cardId);
		if (!options?.preserveRefreshing) {
			this.#syncRefreshing();
		}
	}

	#syncRefreshing(): void {
		const busy =
			this.deferredRefresh ||
			this.activeRequestByCard.size > 0 ||
			[...this.wsWaitByCard.values()].some(
				wait => wait.wsWaitRequestToken !== undefined
			);
		this.isRefreshing.set(busy);
	}
}
